/**
 * Group of Related Content Components.
 */
import { Component, registerComponent } from 'js/component.js';

const componentName = 'alert-banner-config';
const WS = window.sessionStorage;

class AlertBanner extends Component {

    /**
     * Component Setup
     */
    elements = {
        body: document.body
    };

    // Util & State
    alertEvent = new CustomEvent('rockwell-alert', null);
    state = {
        lastModified: null
    };

    /**
     * Initialize Component
     */
    init() {
        const configPagePath = this.element.getAttribute('data-path');
        this.getAlertBannerConfigPage(configPagePath);
    }

    getAlertBannerConfigPage = (url) => {
        fetch(url).then((response) => {
            if (!response.ok) {
                throw new Error('Response failed');
            }
            return response.text();
        }).then((response) => {
            // Parse HTML for alert banner components.
            const alertBannerConfigPage = response;
            const HTMLElement = document.createElement('html');
            HTMLElement.innerHTML = alertBannerConfigPage;
            const alertBannerComponents = [...HTMLElement.querySelectorAll('.alert-banner-new')];
            const alertBannerToDisplay = this.findAlertBannerToDisplay(alertBannerComponents);
            if (alertBannerToDisplay !== -1 && document.querySelector('ra-header')) {
                const header = document.querySelector('ra-header');
                this.checkSessionStorage();
                const lastModified = alertBannerComponents[alertBannerToDisplay].querySelector('.alert-banner-new__container').getAttribute('data-last-modified');
                if (JSON.parse(WS.getItem('rockwell-alert')) !== null && lastModified === JSON.parse(WS.getItem('rockwell-alert')).lastModified) {
                    return;
                }
                header.insertAdjacentElement('beforeBegin', alertBannerComponents[alertBannerToDisplay]);
                alertBannerComponents[alertBannerToDisplay].querySelector('.alert-banner-new__close').addEventListener('click', this.closeAlertBanner);
                alertBannerComponents[alertBannerToDisplay].querySelector('.alert-banner-new__close').addEventListener('keydown', (event) => {
                    if (event.keyCode === 13) {
                        this.closeAlertBanner(event);
                    }
                });
            }
        });
    };

    findAlertBannerToDisplay = (alertBannerComponents) => {
        let pagePath = window.location.pathname.split('.')[0];
        if (pagePath.includes('/content/preview')) {
            pagePath = pagePath.replace('/content/preview', '/content');
        }
        let alertBannerToDisplay = -1;
        alertBannerComponents.forEach((alertBannerComponent, index) => {
            try {
                const container = alertBannerComponent.querySelector('.alert-banner-new__container');

                // Get property values.
                const onTime = container.getAttribute('data-on-time');
                const offTime = container.getAttribute('data-off-time');
                const bannerType = container.getAttribute('data-type');
                const excludePaths = container.getAttribute('data-exclude-paths')
                    ? container.getAttribute('data-exclude-paths').split(',') : [];
                const displayPaths = container.getAttribute('data-display-paths')
                    ? container.getAttribute('data-display-paths').split(',') : [];

                // Validate time window
                const validTime = this.isValidTimeWindow(onTime, offTime);

                // Check path matching (exclude and include paths).
                const excludePathMatch = this.isPathMatch(excludePaths, pagePath, true);
                const displayPathMatch = this.isPathMatch(displayPaths, pagePath, false);

                // Validate if banner should be displayed.
                const displayOnWholeSite = bannerType === 'default';
                const pathMatchingResult = !excludePathMatch && displayPathMatch;
                const noBannerBeingDisplayed = alertBannerToDisplay === -1;
                const displayBanner = validTime && noBannerBeingDisplayed && (displayOnWholeSite || pathMatchingResult);
                if (displayBanner) {
                    alertBannerToDisplay = index;
                }
            } catch (error) {
                console.warn(`Error processing banner component ${index}:`, error);
            }
        });

        return alertBannerToDisplay;
    };

    checkSessionStorage = () => {
        this.state = JSON.parse(WS.getItem('rockwell-alert')) === null ? this.state : JSON.parse(WS.getItem('rockwell-alert'));
    };

    updateSessionStorage = () => {
        WS.setItem('rockwell-alert', JSON.stringify(this.state));
    };

    /**
     * Disable Alert Banner
     */
    disableAlert = (lastModified) => {
        this.element.classList.add('alert-banner-new--closed');
        this.state.lastModified = lastModified;
        this.updateSessionStorage();
        window.dispatchEvent(this.alertEvent);
    };

    closeAlertBanner = (event) => {
        const alertBanner = event.target.closest('.alert-banner-new');
        alertBanner.classList.add('alert-banner-new--closed');
        const lastModified = alertBanner.querySelector('.alert-banner-new__container').getAttribute('data-last-modified');
        this.disableAlert(lastModified);
    };

    /**
     * Check if current page path matches any of the display paths
     * @param {Array} paths - Array of path patterns
     * @param {string} currentPath - Current page path
     * @param {boolean} isCheckingExcludePath - Is this checking for an exclusion path?
     * @returns {boolean} - True if path matches
     */
    isPathMatch = (paths, currentPath, isCheckingExcludePath = false) => {
        if (!paths || paths.length === 0) {
            // No path restrictions means show on all pages, for displayPaths.
            // Otherwise, false.
            return !isCheckingExcludePath;
        }

        return paths.some((displayPath) => {
            if (!displayPath || displayPath.trim() === '') {
                return false;
            }

            try {
                const path = displayPath.split('?includeChildren=')[0];
                const includeChildren = displayPath.split('?includeChildren=')[1] === 'true';

                if (includeChildren) {
                    return currentPath.startsWith(path);
                }
                return currentPath === path;
            } catch (error) {
                console.warn('Error processing display path:', displayPath, error);
                return false;
            }
        });
    };

    /**
     * Check if current time is within the valid time window
     * @param {string} onTime - Start time string
     * @param {string} offTime - End time string
     * @returns {boolean} - True if time is valid
     */
    isValidTimeWindow = (onTime, offTime) => {
        // If no time restrictions, always valid
        if (!onTime && !offTime) {
            return true;
        }

        // If only one time is set, treat as invalid for safety
        if (!onTime || !offTime) {
            console.warn('Banner: Both on-time and off-time must be set if using time restrictions');
            return false;
        }

        try {
            const currentTime = new Date();
            const onTimeDate = new Date(onTime);
            const offTimeDate = new Date(offTime);

            // Validate dates are valid
            if (Number.isNaN(onTimeDate.getTime()) || Number.isNaN(offTimeDate.getTime())) {
                console.warn('Banner: Invalid date format in time restrictions');
                return false;
            }

            return currentTime >= onTimeDate && currentTime <= offTimeDate;
        } catch (error) {
            console.warn('Banner: Error parsing time restrictions:', error);
            return false;
        }
    };

}

registerComponent(`.${componentName}`, AlertBanner);
